import { useMemo } from 'react';
import { remapBooleanConditionReferencesUpwardInScope } from '../../../../../validator/utils/remapReferencesUpwardInScope.ts';
import { useBooleanConditions } from '../../../hooks/useBooleanConditions.tsx';
import { type IRuntimeWidgetAdvancedOption } from '../advanced-select-common/IAdvancedSelectWidget.ts';
import { getUniqueOptionValue } from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { type IAdvancedSingleSelectWidget } from '../advanced-single-select/IAdvancedSingleSelectWidget.ts';
import { multiSelectOptionsObjectName } from '../multi-select/getMultiSelectOptionsObjectName.ts';
import { type IAdvancedMultiSelectWidget } from './IAdvancedMultiSelectWidget.ts';

interface IAdvancedSelectWidgetInput {
  widget: IAdvancedSingleSelectWidget | IAdvancedMultiSelectWidget;
  name: string;
  preview?: boolean;
}

/**
 * Hook per ottenere le opzioni e il dataSource per l'AdvancedMultiSelect.
 *
 * Fare riferimento a {@link useMultiSelectWidgetOptions} per le multi-select
 * classiche
 */
export function useAdvancedMultiSelectWidgetOptions({
  widget,
  name,
  preview,
}: IAdvancedSelectWidgetInput): IRuntimeWidgetAdvancedOption[] {
  const [optionsName, parent] = useMemo(() => {
    const optionsName = multiSelectOptionsObjectName(name);
    return [optionsName, { code: optionsName }];
  }, [name]);

  const optionsWithConditions = useMemo(() => {
    return widget.options.map(option => ({
      option,
      visible: remapBooleanConditionReferencesUpwardInScope(
        parent,
        option.visible,
      ),
      enabled: remapBooleanConditionReferencesUpwardInScope(
        parent,
        option.enabled,
      ),
    }));
  }, [widget.options, parent]);

  const visibles = useBooleanConditions(
    optionsWithConditions.map(o => o.visible),
    { name: `${optionsName}.childVirtualNode`, preview },
  );

  const enableds = useBooleanConditions(
    optionsWithConditions.map(o => o.enabled),
    { name: `${optionsName}.childVirtualNode`, preview },
  );

  const dataSource = useMemo(() => {
    const processedOptions = widget.options
      .map((option, i) => ({
        ...option,
        disabled: !enableds[i].result,
      }))
      .filter((_, index) => visibles[index].result);

    return processedOptions.map(option => {
      const rowData: IRuntimeWidgetAdvancedOption = {
        ...option,
        id: getUniqueOptionValue(widget, option),
        disabled: option.disabled,
      };

      return rowData;
    });
  }, [
    widget.options,
    visibles,
    enableds,
    widget.columns,
    widget.uniqueValueColumnId,
    widget,
  ]);

  return dataSource;
}
